import React, { useState, useEffect } from 'react';
import { FaPlayCircle, FaRegCheckCircle, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import ReactPlayer from 'react-player';
import { startTraining } from '../../../../api/trainingVideoApi';
import authService from '../../../../services/authService';
import { toast } from 'react-toastify';

const VideoModule = ({ module, onComplete, isActive, onVideoStart, onVideoStop }) => {
  const [showVideo, setShowVideo] = useState(isActive);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  const [hasStartedTraining, setHasStartedTraining] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  useEffect(() => {
    setShowVideo(isActive);
  }, [isActive]);

  useEffect(() => {
    const formatDuration = (minutes) => {
      const hrs = Math.floor(minutes / 60);
      const mins = minutes % 60;
      if (hrs > 0) {
        return `${hrs}:${mins.toString().padStart(2, '0')}`;
      }
      return `${mins}:00`;
    };
    setDuration(formatDuration(module.videoDuration));
  }, [module.videoDuration]);

  const handleVideoProgress = (state) => {
    const watchedTime = state.playedSeconds;
    const mandatoryTime = module.mandatoryDuration * 60;
    const progressPercentage = Math.min((watchedTime / mandatoryTime) * 100, 100);
    setProgress(progressPercentage);

    if (watchedTime >= mandatoryTime) {
      onComplete();
    }
  };

  const handleStartTraining = async () => {
    if (!hasStartedTraining) {
      try {
        const userId = authService.getUserId();

        const response = await startTraining({
          trainingId: module.trainingId,
          trainingVideoId: module.id,
          userId: userId,
          startTime: new Date().toISOString()
        });

        // Check if statusCode is not 0 (success)
        if (response.statusCode !== 0) {
          setErrorMessage(response.responseMsg || 'Unable to start training');
          toast.error(response.responseMsg || 'Unable to start training', {
            autoClose: 5000
          });
          return;
        }

        setHasStartedTraining(true);
        setIsPlaying(true);
        onVideoStart();
        setErrorMessage(null);
      } catch (error) {
        console.error('Error starting training:', error);
      }
    } else {
      setIsPlaying(true);
      onVideoStart();
    }
  };

  const handleVideoEnded = () => {
    setShowVideo(false);
    onComplete();
    onVideoStop();
  };

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div className="video-module w-full rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="video-container relative">
        <div className="aspect-video w-full" onContextMenu={(e) => e.preventDefault()}>
          {!isPlaying ? (
            <div className="relative h-0 w-full" style={{ paddingTop: '56.25%' }}>
              <div className="absolute inset-0">
                <img
                  src={module.videoThumbnailUrl}
                  alt={module.title}
                  className="h-full w-full object-contain bg-black"
                />
                <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50">
                  <button
                    onClick={handleStartTraining}
                    className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 sm:py-3 sm:px-6 rounded-lg transition-colors duration-200 flex items-center gap-2 text-sm sm:text-base w-auto max-w-[200px] justify-center"
                  >
                    <FaPlayCircle className="text-lg sm:text-xl" />
                    <span>Play Video</span>
                  </button>
                </div>

                {/* Duration badge */}
                <div className="absolute bottom-2 right-2 rounded bg-black bg-opacity-75 px-2 py-1 text-xs sm:text-sm text-white">
                  {duration}
                </div>

                {/* Completion mark */}
                {module.completed && (
                  <div className="absolute top-2 right-2">
                    <div className="rounded-full bg-white p-2">
                      <FaRegCheckCircle className="text-xl text-green-500" />
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <ReactPlayer
              url={module.videoUrl}
              controls
              width="100%"
              height="100%"
              onEnded={handleVideoEnded}
              onProgress={handleVideoProgress}
              playing={isPlaying}
              config={{
                file: {
                  attributes: {
                    controlsList: 'nodownload',
                    disablePictureInPicture: true
                  }
                }
              }}
            />
          )}
        </div>
        <div className="h-1 w-full bg-gray-200">
          <div
            className="h-full bg-blue-600 transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      <div className="p-3 sm:p-4">
        <h3 className="mb-2 text-lg sm:text-xl font-semibold text-gray-900">{module.title}</h3>

        <div className="border-t border-gray-200 pt-2">
          <div className="mb-2 flex flex-col sm:flex-row sm:items-center sm:justify-between text-xs sm:text-sm text-gray-600 space-y-1 sm:space-y-0">
            <div>
              <span className="font-medium">Video Duration:</span> {duration} mins
            </div>
            <div>
              <span className="font-medium">Mandatory Time:</span> {module.mandatoryDuration} mins
            </div>
          </div>

          <div className="relative">
            <button
              onClick={toggleExpand}
              className="flex w-full items-center justify-between py-2 text-xs sm:text-sm text-gray-600 hover:text-gray-900"
            >
              <span className="font-medium">Description</span>
              {isExpanded ? (
                <FaChevronUp className="text-gray-400" />
              ) : (
                <FaChevronDown className="text-gray-400" />
              )}
            </button>

            <div className={`overflow-hidden transition-all duration-300 ${isExpanded ? 'max-h-96' : 'max-h-0'
              }`}>
              <p className="whitespace-pre-wrap text-xs sm:text-sm text-gray-600 pr-2">
                {module.videoDesc}
              </p>
            </div>
          </div>

          {/* {errorMessage && (
            <p className="mt-2 text-xs sm:text-sm text-red-500">{errorMessage}</p>
          )} */}
        </div>
      </div>
    </div>
  );
};

export default VideoModule;